import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { getLMSStudentName } from "../lib/scorm";
import {
  createUser as apiCreateUser,
  getUser as apiGetUser,
  completeTopic as apiCompleteTopic,
  toggleBookmark as apiToggleBookmark,
  submitQuiz as apiSubmitQuiz,
  markLinkedInShared as apiMarkLinkedInShared,
} from "../lib/api";

const STORAGE_KEY = "steadfast_user_id";
const THEME_KEY = "steadfast_theme";

const UserContext = createContext(null);

export function UserProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [theme, setTheme] = useState("light");

  // Apply theme
  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.classList.remove("dark");
    }
  }, [theme]);

  const createUser = useCallback(async (name) => {
    const u = await apiCreateUser(name);
    try {
      localStorage.setItem(STORAGE_KEY, u.id);
    } catch {}
    setUser(u);
    return u;
  }, []);

  // Load user from localStorage id
  useEffect(() => {
    (async () => {
      try {
        const lmsName = getLMSStudentName();
        const id = localStorage.getItem(STORAGE_KEY);

        if (id) {
          try {
            const u = await apiGetUser(id);
            if (lmsName && u.name !== lmsName) {
              localStorage.removeItem(STORAGE_KEY);
            } else {
              setUser(u);
              setLoading(false);
              return;
            }
          } catch (e) {
            localStorage.removeItem(STORAGE_KEY);
          }
        }

        if (lmsName) {
          await createUser(lmsName);
        }
      } catch (e) {
        try {
          localStorage.removeItem(STORAGE_KEY);
        } catch {}
      } finally {
        setLoading(false);
      }
    })();
  }, [createUser]);

  const completeTopic = useCallback(
    async (moduleId, topicId) => {
      if (!user) return;
      const u = await apiCompleteTopic(user.id, moduleId, topicId);
      setUser(u);
      return u;
    },
    [user]
  );

  const toggleBookmark = useCallback(
    async (topicId) => {
      if (!user) return;
      const u = await apiToggleBookmark(user.id, topicId);
      setUser(u);
      return u;
    },
    [user]
  );

  const submitQuiz = useCallback(
    async (moduleId, topicId, answers) => {
      if (!user) return null;
      const result = await apiSubmitQuiz(user.id, moduleId, topicId, answers);
      // Refresh user to get updated quiz_scores
      try {
        const refreshed = await apiGetUser(user.id);
        setUser(refreshed);
      } catch {}
      return result;
    },
    [user]
  );

  const unlockCertificate = useCallback(async () => {
    if (!user) return;
    const u = await apiMarkLinkedInShared(user.id);
    setUser(u);
    return u;
  }, [user]);

  const logout = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {}
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({
      user,
      loading,
      theme,
      setTheme,
      toggleTheme: () => setTheme((t) => (t === "dark" ? "light" : "dark")),
      createUser,
      completeTopic,
      toggleBookmark,
      submitQuiz,
      unlockCertificate,
      logout,
    }),
    [user, loading, theme, createUser, completeTopic, toggleBookmark, submitQuiz, unlockCertificate, logout]
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

export function useUser() {
  const ctx = useContext(UserContext);
  if (!ctx) throw new Error("useUser must be used within UserProvider");
  return ctx;
}
